/**
 * עגלת בקשות — TASKS 4.2 (הוסף) + 4.3 (גיבוי sessionStorage, עריכה, הסרה).
 *
 * ⭐ **`state` בזיכרון React + גיבוי ב-`sessionStorage`** (הוחלט 09.08.2026,
 *    TASKS 4.3): קבלן שעובר רגע לוואטסאפ וחוזר לא מאבד את העגלה. סגירת הטאב
 *    כן מנקה — ⛔ עדיין לא `localStorage`, לא שומרים מידע לאורך זמן.
 *
 * ⛔ **שורת עגלה ⛔ אף פעם לא מכילה מחיר** (6.5) — רק את מה שהלקוח הזין בפועל.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PricingType } from '../publicApi/catalogApi.js';

export interface CartMeasurements {
  /** `unit`: כמות. `per_meter`: אורך במטרים + כמות. */
  qty: number;
  lengthM?: number;
}

export interface CartItem {
  /** מזהה שורה בעגלה — ⛔ לא ה-sku, כדי שאותו מוצר עם מידות שונות יהיה שתי שורות */
  lineId: string;
  productId: number;
  sku: string;
  productName: string;
  variantId: number | null;
  variantLabel: string | null;
  /** ⭐ DESIGN.md 5.5 — "תמונה קטנה" בכרטיס העגלה. `null` כשלמוצר אין תמונה. */
  productImage: { url: string; alt: string } | null;
  unitLabel: string | null;
  /** ⭐ נדרש כדי לדעת אילו שדות עריכה להציג בעגלה (4.3), בלי לפנות שוב לשרת. */
  pricingType: PricingType;
  minOrderQty: string | null;
  qtyStep: string | null;
  measurements: CartMeasurements;
}

interface CartContextValue {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'lineId'>) => void;
  updateItemMeasurements: (lineId: string, measurements: CartMeasurements) => void;
  removeItem: (lineId: string) => void;
  clearCart: () => void;
}

const STORAGE_KEY = 'noam-cart-v1';

const CartContext = createContext<CartContextValue | null>(null);

/** ⛔ לעולם לא זורק — עגלה פגומה ב-sessionStorage מתעלמת ומתחילה ריקה. */
function loadFromSessionStorage(): CartItem[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadFromSessionStorage());

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ⚠️ sessionStorage חסום/מלא (למשל גלישה פרטית) — גיבוי בלבד, ⛔ לא קריטי לפעולת העגלה
    }
  }, [items]);

  const addItem = useCallback((item: Omit<CartItem, 'lineId'>) => {
    setItems((prev) => [
      ...prev,
      { ...item, lineId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
    ]);
  }, []);

  const updateItemMeasurements = useCallback((lineId: string, measurements: CartMeasurements) => {
    setItems((prev) =>
      prev.map((item) => (item.lineId === lineId ? { ...item, measurements } : item)),
    );
  }, []);

  const removeItem = useCallback((lineId: string) => {
    setItems((prev) => prev.filter((item) => item.lineId !== lineId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo(
    () => ({ items, addItem, updateItemMeasurements, removeItem, clearCart }),
    [items, addItem, updateItemMeasurements, removeItem, clearCart],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart חייב לרוץ בתוך CartProvider');
  return ctx;
}
